import React, { useEffect } from "react";
import SectionToggle from "../../components/sectionToggle/sectionToggle";
import LoadingAnimation from "../../components/loadingAnimation/loadingAnimation";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { UpdateButtons } from "./smartComponents/UpdateButtons";
import { actionCreators } from "./redux/DemographicsSagas";
import PieCharts from "./smartComponents/PieCharts";
import Filters from "./smartComponents/Filters";
import { addSticky } from "../../utils/addSticky";
import TableWithCustomSearch from "../../components/table/coreUiTableWithCustomSearchAndDateConversion/TableWithCustomSearch";
import KBLearnMore from "../../components/learnMore/KBLearnMore";
import { ILEARN_URLS } from "../../constants/ilearnUrls";
import PropTypes from "prop-types";
import ToggleableButton from "../../components/toggleableButton/toggleableButton";

const DemographicsModule = (props) => {
  const dispatch = useDispatch();
  const {
    filters = [],
    selectedFilters,
    isAllSitesMode,
    pieChartsData,
    isLoading,
    updatesLoading,
    isLoadingFilters,
    referralCategory,
    referralCategoryAfterUpdate,
    chartsData: {
      tableData: {
        content: { data = [], colHeaders = [], columns, rawDataLink }
      }
    }
  } = useSelector((state) => state.demographics, shallowEqual);

  const showRawDataLink = selectedFilters.group_by.name === "Not Grouped";

  useEffect(() => {
    if (!isLoading && !isAllSitesMode) {
      addSticky();
    }
  }, [isLoading]);

  const filtersOptions = [
    {
      label: referralCategory ? "Referral Categories" : "Referral Sources",
      name: "selected_categories",
      selectedFilters: selectedFilters.selected_categories,
      options: filters.selected_categories,
      width: "145px"
    },
    {
      label: "ZIP Code",
      name: "zip_codes",
      selectedFilters: selectedFilters.zip_codes,
      options: filters.zip_codes
    },
    {
      label: "Age Groups",
      name: "age_groups",
      selectedFilters: selectedFilters.age_groups,
      options: filters.age_groups
    },
    {
      label: "Gender",
      name: "genders",
      selectedFilters: selectedFilters.genders,
      options: filters.genders
    },
    {
      label: "Last Visit",
      name: "last_visits_months",
      selectedFilters: selectedFilters.last_visits_months,
      options: filters.last_visits_months
    },
    {
      label: "Birth Month",
      name: "birth_months",
      selectedFilters: selectedFilters.birth_months,
      options: filters.birth_months
    }
  ];

  const pieChartsOptions = [
    {
      data: pieChartsData.chartByReferralCategory,
      title: `Clients by referral ${referralCategoryAfterUpdate ? "category" : "source"}`
    },
    { data: pieChartsData.chartByZipCode, title: "Clients by zip code" },
    { data: pieChartsData.chartByAge, title: "Clients by age" },
    { data: pieChartsData.chartByGender, title: "Clients by gender" }
  ];

  useEffect(() => {
    dispatch(actionCreators.fetchCurrentUser(props));
  }, []);
  const content = () => {
    return isAllSitesMode ? (
      <p className="error error--big" style={{ ...errorMessageStyles }}>
        Not Available in All Sites View, when user has access to more than 100 locations
      </p>
    ) : (
      <div>
        <div id="sticky-select-bar" className="edit-selections-bar">
          <div className="selection-bar-filters">
            <div className="top-filter">
              <div className="top-filter__left">
                <div className="top-filter__left-top">
                  <Filters
                    allSelectedPlaceholder="All Clients"
                    selectAllText="All Clients"
                    label="Client Type"
                    name="client_types"
                    selectedFilters={selectedFilters.client_types}
                    options={filters.client_types}
                    numberDisplayed={1}
                  />
                  <div className="select-box" style={{ width: "120px" }}>
                    <Filters
                      label="Group By"
                      name="group_by"
                      isLoadingFilters={isLoadingFilters}
                      selectedFilters={selectedFilters.group_by}
                      options={filters.group_by}
                      numberDisplayed={0}
                      includeFilter={false}
                      includeSelectAll={false}
                      enableOnly={false}
                      width="160px"
                    />
                  </div>
                </div>
                <div className="top-filter__left-bottom">
                  <div className="select-box-no-width categories">
                    <p>Referral Category</p>
                    <ToggleableButton
                      isRadio
                      isActive={referralCategory}
                      title="referal-category"
                      onClick={(isActive) =>
                        dispatch(actionCreators.updateReferralToggle(isActive))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="top-filter__right">
                {filtersOptions.map(({ label, name, selectedFilters, options, width }) => (
                  <Filters
                    isLoadingFilters={name === "selected_categories" && isLoadingFilters}
                    key={label}
                    label={label}
                    name={name}
                    selectedFilters={selectedFilters}
                    options={options}
                    numberDisplayed={0}
                    width={width}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <UpdateButtons />
          </div>
        </div>
        <div data-testid="charts-container" className="chart-root">
          {updatesLoading ? (
            <LoadingAnimation text="Updating data..." />
          ) : (
            <>
              <div data-testid="pie-charts-container" className="pie-chart">
                {pieChartsOptions.map(({ data, title }) => {
                  return <PieCharts key={title} data={data} title={title} />;
                })}
              </div>
              <TableWithCustomSearch
                fileName="clients"
                data={data}
                columns={columns}
                colHeaders={colHeaders}
                rawDataLink={showRawDataLink ? rawDataLink : null}
                itemsPerPage={20}
              />
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div id="DemographicsModule-root">
      <SectionToggle
        height={690}
        customClass="withSticky"
        title="Demographics"
        isLoading={isLoading}
        content={content()}
        learnMore={
          <KBLearnMore
            title="Demographics"
            description="This report shows demographic details about your clients, helping you filter and sort member data to understand where clients come from and how they engage with your business. These insights help you tailor marketing strategies, improve targeting, and better serve core audience groups."
            kbUrl={ILEARN_URLS.demographics.demographics}
          />
        }
      />
    </div>
  );
};
export default DemographicsModule;

const errorMessageStyles = {
  textAlign: "center",
  fontSize: "48px",
  wordSpacing: 0,
  lineHeight: "60px"
};

DemographicsModule.propTypes = {
  params: PropTypes.object
};
